import { useState, useEffect, useRef } from "react"
import styled from "styled-components"
import { User, Bell, Moon, LogIn, Download, Trash2, Shield, Globe, Mail, Camera, Database, Settings as SettingsIcon, Key, Save, Lock, ChevronRight, Crown, Calendar as CalendarIcon } from "lucide-react"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"
import { useNavigate } from "react-router-dom"
import toast from 'react-hot-toast'

const Container = styled.div`
  padding: 32px;
  background-color: ${props => props.theme.backgroundColor};
  min-height: calc(100vh - 80px);
  width: 100%;

  @media (max-width: 768px) {
    padding: 20px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 22px;
`

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 6px 0;
`

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  margin: 0;
`

const TabsRow = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 22px;
  overflow-x: auto;
  border-bottom: 1px solid ${props => props.theme.border};
`

const TabItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: ${props => props.$active ? 'linear-gradient(135deg, rgba(124,58,237,0.10), rgba(37,99,235,0.10))' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#7c3aed' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => props.$active ? '#7c3aed' : props.theme.textSecondary};
  font-weight: ${props => props.$active ? 600 : 500};
  font-size: 14px;
  white-space: nowrap;
  border-radius: 8px 8px 0 0;
  margin-bottom: -1px;

  &:hover {
    color: #7c3aed;
    background: ${props => props.$active ? 'linear-gradient(135deg, rgba(124,58,237,0.14), rgba(37,99,235,0.14))' : 'rgba(124,58,237,0.06)'};
  }
`

const SplitLayout = styled.div`
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const ProfileCard = styled.div`
  background: linear-gradient(135deg, rgba(167,139,250,0.18) 0%, rgba(196,181,253,0.18) 100%);
  border: 1px solid rgba(167,139,250,0.25);
  border-radius: 16px;
  padding: 22px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
`

const ProfileAvatarWrap = styled.div`
  position: relative;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  background: ${props => props.theme.cardBackground};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  box-shadow: 0 8px 20px rgba(124,58,237,0.18);
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }
`

const AvatarCameraBtn = styled.button`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #7c3aed;
  border: 2px solid white;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  &:hover { background: #6d28d9; }
`

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ProfileNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const ProfileName = styled.span`
  font-size: 17px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
`

const PremiumBadge = styled.span`
  background: rgba(124,58,237,0.15);
  color: #7c3aed;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
`

const ProfileEmail = styled.div`
  font-size: 12.5px;
  color: ${props => props.theme.textSecondary};
  margin-top: 4px;
`

const ProfileIntro = styled.p`
  margin: 8px 0 0 0;
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  line-height: 1.4;
`

const SummaryCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 14px;
  padding: 18px;
`

const CardHeading = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin-bottom: 12px;
`

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid ${props => props.theme.borderLight};
  &:last-child { border-bottom: none; }
`

const SummaryIconWrap = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(124,58,237,0.10);
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const SummaryText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  .label {
    font-size: 11px;
    color: ${props => props.theme.textMuted};
  }
  .value {
    font-size: 13px;
    color: ${props => props.theme.textPrimary};
    font-weight: 500;
    word-break: break-all;
  }
`

const ActionRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  background: transparent;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  color: ${props => props.theme.textPrimary};
  transition: all 0.15s;
  margin-bottom: 8px;
  text-align: left;
  &:last-child { margin-bottom: 0; }

  &:hover {
    border-color: ${props => props.$danger ? '#ef4444' : '#7c3aed'};
    background: ${props => props.$danger ? 'rgba(239,68,68,0.04)' : 'rgba(124,58,237,0.04)'};
  }

  .icon-wrap {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: ${props => props.$danger ? 'rgba(239,68,68,0.10)' : 'rgba(124,58,237,0.10)'};
    color: ${props => props.$danger ? '#ef4444' : '#7c3aed'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .label {
    flex: 1;
    color: ${props => props.$danger ? '#ef4444' : props.theme.textPrimary};
    font-weight: 500;
  }
  .chev {
    color: ${props => props.theme.textMuted};
    flex-shrink: 0;
  }
`

const SectionCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 14px;
  padding: 24px;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.textPrimary};
  margin: 0 0 18px 0;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
`

const Label = styled.label`
  font-weight: 600;
  font-size: 13px;
  color: ${props => props.theme.textPrimary};
`

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  font-size: 14px;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
  }
  &::placeholder { color: ${props => props.theme.textMuted}; }
`

const Textarea = styled.textarea`
  padding: 12px 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  font-size: 14px;
  min-height: 130px;
  resize: vertical;
  background-color: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124,58,237,0.10);
  }
  &::placeholder { color: ${props => props.theme.textMuted}; }
`

const CharCount = styled.div`
  position: absolute;
  bottom: 8px;
  right: 12px;
  font-size: 11px;
  color: ${props => props.theme.textMuted};
  pointer-events: none;
`

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;

  input { opacity: 0; width: 0; height: 0; }
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: ${props => props.theme.border};
    transition: .3s;
    border-radius: 24px;
  }
  .slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
  }
  input:checked + .slider { background-color: #7c3aed; }
  input:checked + .slider:before { transform: translateX(20px); }
`

const ToggleOption = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid ${props => props.theme.borderLight};
  &:last-of-type { border-bottom: none; }
`

const ToggleText = styled.div`
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  font-weight: 500;
`

const ToggleDescription = styled.div`
  font-size: 12px;
  color: ${props => props.theme.textSecondary};
  margin-top: 2px;
`

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #7c3aed;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 11px 20px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  align-self: flex-start;

  &:hover { background: #6d28d9; }
  &:disabled { background: ${props => props.theme.textMuted}; cursor: not-allowed; }
`

const HiddenFileInput = styled.input`
  display: none;
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.textSecondary};
`

const LoginRequiredMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 40px;
  text-align: center;

  .icon { color: ${props => props.theme.textMuted}; margin-bottom: 16px; }
  .title {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${props => props.theme.textPrimary};
  }
  .text {
    color: ${props => props.theme.textSecondary};
    margin-bottom: 24px;
  }
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
`

const StatBox = styled.div`
  background: ${props => props.theme.hoverBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  .label { font-size: 12px; color: ${props => props.theme.textSecondary}; }
  .value { font-size: 18px; font-weight: 700; color: ${props => props.theme.textPrimary}; }
`

const DangerZone = styled.div`
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  background-color: #fef2f2;
`

const DangerTitle = styled.h3`
  color: #dc2626;
  margin: 0 0 8px 0;
  font-size: 15px;
  font-weight: 700;
`

const DangerText = styled.p`
  color: #991b1b;
  margin: 0 0 12px 0;
  font-size: 13px;
`

const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;

  &:hover { background-color: #dc2626; }
  &:disabled { background-color: #fca5a5; cursor: not-allowed; }
`

const HelpButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 10px;
  background: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  text-align: left;

  &:hover {
    border-color: #7c3aed;
    background: rgba(124,58,237,0.04);
  }
`

const TABS = [
  { key: 'profile', label: '프로필 관리', Icon: User },
  { key: 'subscription', label: '구독 관리', Icon: Crown },
  { key: 'display', label: '화면 설정', Icon: Moon },
  { key: 'notifications', label: '환경 설정', Icon: Bell },
  { key: 'stats', label: '학습 통계', Icon: Database },
  { key: 'data', label: '데이터 관리', Icon: Download },
  { key: 'api', label: 'API 설정', Icon: Key },
  { key: 'permissions', label: '앱 권한 설정', Icon: Shield },
]

function formatJoinDate(dateStr) {
  if (!dateStr) return '-'
  try {
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return '-'
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}. ${mm}. ${dd}`
  } catch {
    return '-'
  }
}

export default function Settings() {
  const { isLoggedIn, user, updateUserProfile, logout } = useAuth();
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("profile");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalLikes: 0,
    totalComments: 0,
    accountAge: 0
  });
  const [formData, setFormData] = useState({
    username: "",
    full_name: "",
    email: "",
    bio: "",
    location: "",
    website: "",
    avatar_url: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    emailNotifications: true,
    pushNotifications: true,
    likeNotifications: true,
    commentNotifications: true,
    followNotifications: true,
    mentionNotifications: true,
    privateAccount: false,
    showEmail: false,
    showLocation: false,
    allowTagging: true,
    darkMode: false,
    language: "ko",
    twoFactorEnabled: false,
    loginAlerts: true,
    claudeApiToken: ""
  });

  const fileInputRef = useRef(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const fetchUserStats = async () => {
    try {
      const response = await fetch(`/api/user/stats/${user.id}`, {
        credentials: 'include'
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.success) {
        setStats({
          totalPosts: data.stats.posts || 0,
          totalLikes: data.stats.total_likes || 0,
          totalComments: data.stats.total_comments || 0,
          accountAge: Math.floor((new Date() - new Date(user.created_at)) / (1000 * 60 * 60 * 24)) || 0
        });
      }
    } catch (error) {
      console.log('통계 로드 불가능 (선택적 기능)');
    }
  };

  useEffect(() => {
    if (user) {
      const loadClaudeToken = async () => {
        try {
          const response = await fetch(`/auth/claude-token/${user.id}`, {
            credentials: 'include'
          });
          const data = await response.json();
          const claudeToken = data.success ? data.data.claudeApiToken : '';

          setFormData(prev => ({
            ...prev,
            username: user.username || "",
            full_name: user.full_name || "",
            email: user.email || "",
            bio: user.bio || "",
            location: user.location || "",
            website: user.website || "",
            avatar_url: user.avatar_url || "",
            emailNotifications: user.emailNotifications || true,
            pushNotifications: user.pushNotifications || true,
            likeNotifications: user.likeNotifications !== false,
            commentNotifications: user.commentNotifications !== false,
            followNotifications: user.followNotifications !== false,
            mentionNotifications: user.mentionNotifications !== false,
            privateAccount: user.privateAccount || false,
            showEmail: user.showEmail || false,
            showLocation: user.showLocation || false,
            allowTagging: user.allowTagging !== false,
            darkMode: user.darkMode || false,
            language: user.language || "ko",
            twoFactorEnabled: user.twoFactorEnabled || false,
            loginAlerts: user.loginAlerts !== false,
            claudeApiToken: claudeToken
          }));
        } catch (error) {
          console.error('Claude 토큰 로드 실패:', error);
          setFormData(prev => ({
            ...prev,
            username: user.username || "",
            full_name: user.full_name || "",
            email: user.email || "",
            bio: user.bio || "",
            avatar_url: user.avatar_url || "",
            language: user.language || "ko",
            claudeApiToken: ''
          }));
        }
      };

      loadClaudeToken();
      fetchUserStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (!isLoggedIn) {
    return (
      <Container theme={theme}>
        <SectionCard theme={theme}>
          <LoginRequiredMessage theme={theme}>
            <User size={48} className="icon" />
            <div className="title">로그인이 필요합니다</div>
            <div className="text">설정을 변경하려면 로그인이 필요합니다.</div>
            <PrimaryButton theme={theme} onClick={() => navigate('/')}>
              <LogIn size={16} />
              홈페이지로 이동
            </PrimaryButton>
          </LoginRequiredMessage>
        </SectionCard>
      </Container>
    );
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        setAvatarPreview(reader.result);
        await updateUserProfile({ avatar_url: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let updatedAvatarUrl = formData.avatar_url;
      if (avatarPreview) updatedAvatarUrl = avatarPreview;

      const profileData = {
        user_name: formData.full_name,
        email: formData.email,
        phone: formData.location,
        profile_image: updatedAvatarUrl,
        bio: formData.bio
      };

      const response = await fetch(`/api/profile/profile/${user.username}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      });

      const result = await response.json();
      if (result.success) {
        await updateUserProfile({
          ...user,
          full_name: result.user.full_name,
          email: result.user.email,
          avatar_url: result.user.avatar_url
        });
        toast.success('설정이 저장되었습니다.');
      } else {
        toast.error(result.message || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('설정 저장 오류:', error);
      toast.error('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('새 비밀번호와 비밀번호 확인이 일치하지 않습니다.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch('/user/change-password', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        })
      });
      const result = await response.json();
      if (result.success) {
        toast.success('비밀번호가 변경되었습니다.');
        setFormData({ ...formData, currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        toast.error(result.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      toast.error('비밀번호 변경 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      setLoading(true);
      const responses = await Promise.all([
        fetch(`/post/user/${user.id}?limit=1000`, { credentials: 'include' }),
        fetch('/bookmark/list?limit=1000', { credentials: 'include' }),
        fetch(`/follow/followers/${user.id}`, { credentials: 'include' }),
        fetch(`/follow/following/${user.id}`, { credentials: 'include' })
      ]);
      const [postsData, bookmarksData, followersData, followingData] = await Promise.all(
        responses.map(response => response.json())
      );
      const exportData = {
        user: {
          id: user.id, username: user.username, email: user.email,
          bio: user.bio, created_at: user.created_at, stats
        },
        posts: postsData.success ? postsData.posts : [],
        bookmarks: bookmarksData.success ? bookmarksData.bookmarks : [],
        followers: followersData.success ? followersData.followers : [],
        following: followingData.success ? followingData.following : [],
        exportDate: new Date().toISOString()
      };
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `antsnest_data_${user.username}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('데이터 내보내기가 완료되었습니다.');
    } catch (error) {
      console.error('데이터 내보내기 오류:', error);
      toast.error('데이터 내보내기 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    const confirmText = `계정을 삭제하려면 "${user.username}"을 입력하세요:`;
    const userInput = window.prompt(confirmText);
    if (userInput !== user.username) {
      toast.error('사용자명이 일치하지 않습니다.');
      return;
    }
    if (!window.confirm('정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return;
    try {
      setLoading(true);
      const response = await fetch('/user/delete', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        toast.success('계정이 삭제되었습니다.');
        await logout();
        navigate('/');
      } else {
        toast.error(data.message || '계정 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('계정 삭제 오류:', error);
      toast.error('계정 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToPasswordSection = () => {
    setActiveSection('profile');
    setTimeout(() => {
      const el = document.getElementById('password-section');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const renderRight = () => {
    if (loading) {
      return (
        <SectionCard theme={theme}>
          <LoadingIndicator theme={theme}>로딩 중...</LoadingIndicator>
        </SectionCard>
      );
    }

    switch (activeSection) {
      case "profile":
        return (
          <>
            <SectionCard theme={theme}>
              <SectionTitle theme={theme}>개인 정보</SectionTitle>
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label theme={theme} htmlFor="full_name">이름</Label>
                  <Input theme={theme} type="text" id="full_name" name="full_name"
                    value={formData.full_name} onChange={handleChange} placeholder="이름" />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme} htmlFor="email">이메일</Label>
                  <Input theme={theme} type="email" id="email" name="email"
                    value={formData.email} onChange={handleChange} placeholder="이메일" />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme} htmlFor="bio">자기소개</Label>
                  <Textarea theme={theme} id="bio" name="bio" maxLength={200}
                    value={formData.bio} onChange={handleChange}
                    placeholder="자신에 대해 소개해주세요. (선택사항)" />
                  <CharCount theme={theme}>{(formData.bio || '').length} / 200</CharCount>
                </FormGroup>
                <PrimaryButton theme={theme} type="submit" disabled={loading}>
                  <Save size={16} />
                  변경사항 저장
                </PrimaryButton>
              </Form>
            </SectionCard>

            <SectionCard theme={theme} id="password-section">
              <SectionTitle theme={theme}>비밀번호 변경</SectionTitle>
              <Form onSubmit={handlePasswordChange}>
                <FormGroup>
                  <Label theme={theme} htmlFor="currentPassword">현재 비밀번호</Label>
                  <Input theme={theme} type="password" id="currentPassword" name="currentPassword"
                    value={formData.currentPassword} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme} htmlFor="newPassword">새 비밀번호</Label>
                  <Input theme={theme} type="password" id="newPassword" name="newPassword"
                    value={formData.newPassword} onChange={handleChange} />
                </FormGroup>
                <FormGroup>
                  <Label theme={theme} htmlFor="confirmPassword">비밀번호 확인</Label>
                  <Input theme={theme} type="password" id="confirmPassword" name="confirmPassword"
                    value={formData.confirmPassword} onChange={handleChange} />
                </FormGroup>
                <PrimaryButton theme={theme} type="submit" disabled={loading}>
                  <Lock size={16} />
                  비밀번호 변경
                </PrimaryButton>
              </Form>
            </SectionCard>
          </>
        );

      case "subscription":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>구독 관리</SectionTitle>
            <ToggleDescription theme={theme} style={{ marginBottom: 18 }}>
              현재 구독 상태와 플랜을 확인하고 변경할 수 있습니다.
            </ToggleDescription>
            <PrimaryButton theme={theme} onClick={() => navigate('/pricing')}>
              <Crown size={16} />
              구독 관리하기
            </PrimaryButton>
          </SectionCard>
        );

      case "display":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>화면 설정</SectionTitle>
            <ToggleOption theme={theme}>
              <div>
                <ToggleText theme={theme}>다크 모드</ToggleText>
                <ToggleDescription theme={theme}>어두운 테마로 눈의 피로를 줄일 수 있습니다.</ToggleDescription>
              </div>
              <ToggleSwitch theme={theme}>
                <input type="checkbox" checked={isDarkMode} onChange={toggleTheme} />
                <span className="slider"></span>
              </ToggleSwitch>
            </ToggleOption>
          </SectionCard>
        );

      case "notifications":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>환경 설정</SectionTitle>
            <Form onSubmit={handleSubmit}>
              <ToggleOption theme={theme}>
                <div>
                  <ToggleText theme={theme}>이메일 알림</ToggleText>
                  <ToggleDescription theme={theme}>이메일로 중요 알림을 받습니다.</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input type="checkbox" name="emailNotifications"
                    checked={formData.emailNotifications} onChange={handleChange} />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <ToggleOption theme={theme}>
                <div>
                  <ToggleText theme={theme}>푸시 알림</ToggleText>
                  <ToggleDescription theme={theme}>브라우저와 앱에서 푸시 알림을 받습니다.</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input type="checkbox" name="pushNotifications"
                    checked={formData.pushNotifications} onChange={handleChange} />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <ToggleOption theme={theme}>
                <div>
                  <ToggleText theme={theme}>좋아요 알림</ToggleText>
                  <ToggleDescription theme={theme}>내 콘텐츠에 좋아요가 눌리면 알려드립니다.</ToggleDescription>
                </div>
                <ToggleSwitch theme={theme}>
                  <input type="checkbox" name="likeNotifications"
                    checked={formData.likeNotifications} onChange={handleChange} />
                  <span className="slider"></span>
                </ToggleSwitch>
              </ToggleOption>
              <PrimaryButton theme={theme} type="submit" disabled={loading}>
                <Save size={16} /> 저장
              </PrimaryButton>
            </Form>
          </SectionCard>
        );

      case "stats":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>학습 통계</SectionTitle>
            <ToggleDescription theme={theme} style={{ marginBottom: 14 }}>
              학습 진행 상황과 성취도를 한눈에 확인할 수 있습니다.
            </ToggleDescription>
            <StatGrid>
              <StatBox theme={theme}>
                <span className="label">완료한 게임</span>
                <span className="value">{stats.totalPosts || 0}</span>
              </StatBox>
              <StatBox theme={theme}>
                <span className="label">총 학습 시간</span>
                <span className="value">{stats.totalLikes || 0}시간</span>
              </StatBox>
              <StatBox theme={theme}>
                <span className="label">평균 점수</span>
                <span className="value">{stats.totalComments || 0}점</span>
              </StatBox>
            </StatGrid>
          </SectionCard>
        );

      case "data":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>데이터 관리</SectionTitle>
            <ToggleDescription theme={theme} style={{ marginBottom: 18 }}>
              게임 기록, 학습 데이터, 사용자 설정 등을 내보낼 수 있습니다.
            </ToggleDescription>
            <PrimaryButton theme={theme} onClick={handleExportData} disabled={loading}>
              <Download size={16} />
              데이터 내보내기
            </PrimaryButton>
          </SectionCard>
        );

      case "api":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>API 설정</SectionTitle>
            <ToggleDescription theme={theme} style={{ marginBottom: 18 }}>
              Claude AI API를 사용하기 위한 인증 토큰을 설정합니다. 토큰은 안전하게 서버에 저장됩니다.
            </ToggleDescription>
            <Form onSubmit={async (e) => {
              e.preventDefault();
              const token = formData.claudeApiToken.trim();
              if (token && !token.startsWith('sk-ant-')) {
                toast.error('올바른 Claude API 토큰 형식이 아닙니다. "sk-ant-"로 시작해야 합니다.');
                return;
              }
              if (token && (token.length < 100 || token.length > 150)) {
                toast.error('API 토큰 길이가 올바르지 않습니다.');
                return;
              }
              setLoading(true);
              try {
                const response = await fetch('/auth/update-claude-token', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ userId: user.id, claudeApiToken: token })
                });
                const data = await response.json();
                if (data.success) toast.success('API 토큰이 저장되었습니다.');
                else toast.error(data.error || 'API 토큰 저장에 실패했습니다.');
              } catch (error) {
                toast.error('API 토큰 저장 중 오류가 발생했습니다.');
              } finally {
                setLoading(false);
              }
            }}>
              <FormGroup>
                <Label theme={theme} htmlFor="claudeApiToken">API 토큰</Label>
                <Input theme={theme} type="password" id="claudeApiToken" name="claudeApiToken"
                  value={formData.claudeApiToken} onChange={handleChange} placeholder="sk-ant-..." />
              </FormGroup>
              <div style={{ display: 'flex', gap: 12 }}>
                <PrimaryButton theme={theme} type="submit" disabled={loading}>
                  <Save size={16} /> 저장
                </PrimaryButton>
                {formData.claudeApiToken && (
                  <DangerButton type="button" disabled={loading} onClick={async () => {
                    setLoading(true);
                    try {
                      const response = await fetch('/auth/update-claude-token', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ userId: user.id, claudeApiToken: '' })
                      });
                      const data = await response.json();
                      if (data.success) {
                        setFormData({ ...formData, claudeApiToken: '' });
                        toast.success('API 토큰이 삭제되었습니다.');
                      } else toast.error(data.error || 'API 토큰 삭제에 실패했습니다.');
                    } catch {
                      toast.error('API 토큰 삭제 중 오류가 발생했습니다.');
                    } finally { setLoading(false); }
                  }}>
                    <Trash2 size={16} /> 삭제
                  </DangerButton>
                )}
              </div>
            </Form>
          </SectionCard>
        );

      case "permissions":
        return (
          <SectionCard theme={theme}>
            <SectionTitle theme={theme}>앱 권한 설정</SectionTitle>
            <ToggleDescription theme={theme} style={{ marginBottom: 14 }}>
              앱에서 사용하는 권한들을 관리하고 설정할 수 있습니다.
            </ToggleDescription>
            <ToggleOption theme={theme}>
              <div>
                <ToggleText theme={theme}>위치 서비스</ToggleText>
                <ToggleDescription theme={theme}>AR 기능을 위한 위치 정보 접근</ToggleDescription>
              </div>
              <ToggleSwitch theme={theme}>
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </ToggleSwitch>
            </ToggleOption>
            <ToggleOption theme={theme}>
              <div>
                <ToggleText theme={theme}>카메라 권한</ToggleText>
                <ToggleDescription theme={theme}>AR 카메라 및 사진 촬영 기능</ToggleDescription>
              </div>
              <ToggleSwitch theme={theme}>
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </ToggleSwitch>
            </ToggleOption>
          </SectionCard>
        );

      default:
        return null;
    }
  };

  const accountTypeLabel = user?.is_premium ? '프리미엄 플랜' : '무료 플랜';
  const langLabel = formData.language === 'ko' ? '한국어 (ko)' : formData.language;
  const joinDate = formatJoinDate(user?.created_at);
  const introText = formData.bio && formData.bio.trim()
    ? formData.bio
    : 'AR ESCAPE와 함께 즐거운 경험을 만들어보세요!';

  return (
    <Container theme={theme}>
      <PageHeader>
        <PageTitle theme={theme}>프로필 설정</PageTitle>
        <PageSubtitle theme={theme}>계정 정보를 관리하고 개인 설정을 변경할 수 있습니다.</PageSubtitle>
      </PageHeader>

      <TabsRow theme={theme}>
        {TABS.map(t => {
          const Icon = t.Icon;
          return (
            <TabItem key={t.key} theme={theme} $active={activeSection === t.key}
              onClick={() => setActiveSection(t.key)}>
              <Icon size={16} />
              {t.label}
            </TabItem>
          );
        })}
      </TabsRow>

      <SplitLayout>
        <LeftCol>
          <ProfileCard theme={theme}>
            <ProfileAvatarWrap theme={theme}>
              <img
                src={avatarPreview || formData.avatar_url || "/placeholder.svg?height=84&width=84"}
                alt="프로필 이미지"
              />
              <AvatarCameraBtn type="button" onClick={handleAvatarClick} aria-label="프로필 이미지 변경">
                <Camera size={14} />
              </AvatarCameraBtn>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
              />
            </ProfileAvatarWrap>
            <ProfileInfo>
              <ProfileNameRow>
                <ProfileName theme={theme}>{formData.full_name || user?.username || '사용자'}</ProfileName>
                {user?.is_premium && <PremiumBadge>프리미엄 플랜</PremiumBadge>}
              </ProfileNameRow>
              <ProfileEmail theme={theme}>{formData.email}</ProfileEmail>
              <ProfileIntro theme={theme}>{introText}</ProfileIntro>
            </ProfileInfo>
          </ProfileCard>

          <SummaryCard theme={theme}>
            <CardHeading theme={theme}>계정 요약</CardHeading>
            <SummaryRow theme={theme}>
              <SummaryIconWrap><Mail size={15} /></SummaryIconWrap>
              <SummaryText theme={theme}>
                <span className="label">이메일</span>
                <span className="value">{formData.email || '-'}</span>
              </SummaryText>
            </SummaryRow>
            <SummaryRow theme={theme}>
              <SummaryIconWrap><User size={15} /></SummaryIconWrap>
              <SummaryText theme={theme}>
                <span className="label">이름</span>
                <span className="value">{formData.full_name || '-'}</span>
              </SummaryText>
            </SummaryRow>
            <SummaryRow theme={theme}>
              <SummaryIconWrap><Crown size={15} /></SummaryIconWrap>
              <SummaryText theme={theme}>
                <span className="label">계정 유형</span>
                <span className="value">{accountTypeLabel}</span>
              </SummaryText>
            </SummaryRow>
            <SummaryRow theme={theme}>
              <SummaryIconWrap><Globe size={15} /></SummaryIconWrap>
              <SummaryText theme={theme}>
                <span className="label">언어</span>
                <span className="value">{langLabel}</span>
              </SummaryText>
            </SummaryRow>
            <SummaryRow theme={theme}>
              <SummaryIconWrap><CalendarIcon size={15} /></SummaryIconWrap>
              <SummaryText theme={theme}>
                <span className="label">가입일</span>
                <span className="value">{joinDate}</span>
              </SummaryText>
            </SummaryRow>
          </SummaryCard>

          <SummaryCard theme={theme}>
            <CardHeading theme={theme}>빠른 액션</CardHeading>
            <ActionRow theme={theme} type="button" onClick={scrollToPasswordSection}>
              <span className="icon-wrap"><Lock size={14} /></span>
              <span className="label">비밀번호 변경</span>
              <ChevronRight size={16} className="chev" />
            </ActionRow>
            <ActionRow theme={theme} type="button" $danger onClick={handleDeleteAccount}>
              <span className="icon-wrap"><Trash2 size={14} /></span>
              <span className="label">계정 삭제</span>
              <ChevronRight size={16} className="chev" />
            </ActionRow>
          </SummaryCard>
        </LeftCol>

        <RightCol>
          {renderRight()}
        </RightCol>
      </SplitLayout>
    </Container>
  );
}
